import React from 'react';
import {
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  Box,
  Button,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  EditIcon,
  MoreHorizontalIcon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TablePaginationProps,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  TrashIcon,
} from '@razorpay/blade/components';
import { EMPLOYEE_FIELDS_SKELETON_ROW_COUNT } from '../constants';
import { EmployeeFieldsTableProps } from '../types';
import {
  formatEmployeeFieldsPaginationLabel,
  getFieldTypeLabel,
  groupFieldsBySection,
} from '../utils';
import { EmployeeFieldsTableSurface } from '../styles';

export const EmployeeFieldsTable = ({
  fields,
  emptyMessage,
  toolbarActions,
  toolbarTitle,
  onEdit,
  onDelete,
  isLoading,
  isFetching,
  isError,
  onRetry,
  currentPage,
  pageSize,
  totalFields,
  onPageChange,
  onPageSizeChange,
}: EmployeeFieldsTableProps) => {
  const groupedFields = groupFieldsBySection(fields);
  const tableFields = Object.values(groupedFields).flat();

  return (
    <EmployeeFieldsTableSurface>
      <Table
        isRefreshing={isFetching && !isLoading}
        data={{ nodes: tableFields }}
        gridTemplateColumns="minmax(320px, 1.8fr) minmax(152px, 0.75fr) minmax(112px, 0.55fr) 72px"
        rowDensity="comfortable"
        showBorderedCells={false}
        pagination={
          totalFields > 0 ? (
            <TablePagination
              currentPage={currentPage}
              defaultPageSize={pageSize as TablePaginationProps['defaultPageSize']}
              paginationType="server"
              showLabel
              label={formatEmployeeFieldsPaginationLabel({
                currentPage,
                pageSize,
                currentCount: tableFields.length,
                total: totalFields,
              })}
              showPageSizePicker
              totalItemCount={totalFields}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={toolbarTitle}>
            <TableToolbarActions>{toolbarActions}</TableToolbarActions>
          </TableToolbar>
        }>
        {(fieldsToRender) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Field</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Required</TableHeaderCell>
                <TableHeaderCell textAlign="right"> </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            {isLoading ? (
              <TableBody>
                {Array.from({ length: EMPLOYEE_FIELDS_SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                  <TableRow
                    key={`employee-fields-skeleton-${rowIndex}`}
                    item={{ id: rowIndex }}
                    isDisabled>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap="spacing.2" paddingY="spacing.3">
                        <Skeleton width="70%" height="spacing.5" />
                        <Skeleton width="28%" height="spacing.4" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Skeleton width="64px" height="spacing.5" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="40px" height="spacing.5" />
                    </TableCell>
                    <TableCell textAlign="right">
                      <Box display="flex" justifyContent="flex-end">
                        <Skeleton width="spacing.7" height="spacing.7" />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                {isError ? (
                  <TableRow item={{ id: 'error-employee-fields' }} isDisabled>
                    <TableCell gridColumnStart={1} gridColumnEnd={5}>
                      <Box
                        minHeight="144px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap="spacing.4">
                        <Text color="surface.text.gray.muted">
                          Could not load employee fields. Please retry.
                        </Text>
                        <Button variant="secondary" onClick={onRetry}>
                          Retry
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : fieldsToRender.length === 0 ? (
                  <TableRow item={{ id: 'empty-employee-fields' }} isDisabled>
                    <TableCell gridColumnStart={1} gridColumnEnd={5}>
                      <Box
                        minHeight="144px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        paddingX="spacing.6">
                        <Text color="surface.text.gray.muted">{emptyMessage}</Text>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  fieldsToRender.map((field, index) => {
                    const previousField = fieldsToRender[index - 1];
                    const shouldShowSection =
                      !previousField || previousField.section !== field.section;

                    return (
                      <React.Fragment key={field.id}>
                        {shouldShowSection ? (
                          <TableRow item={{ id: `section-${field.section}` }} isDisabled>
                            <TableCell>
                              <Box
                                minHeight="60px"
                                display="flex"
                                alignItems="center"
                                data-analytics-section="field-section-separator">
                                <Text
                                  size="medium"
                                  weight="semibold"
                                  color="surface.text.gray.muted">
                                  {field.section}
                                </Text>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Text> </Text>
                            </TableCell>
                            <TableCell>
                              <Text> </Text>
                            </TableCell>
                            <TableCell>
                              <Text> </Text>
                            </TableCell>
                          </TableRow>
                        ) : null}
                        <TableRow item={field}>
                          <TableCell>
                            <Box
                              display="flex"
                              flexDirection="column"
                              gap="spacing.1"
                              minWidth="0px">
                              <Text color="surface.text.staticWhite.subtle">{field.label}</Text>
                              <Box>
                                <Text size="small" color="surface.text.gray.muted">
                                  {field.origin === 'CUSTOM' ? 'Custom' : 'Predefined'}
                                </Text>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Text color="surface.text.staticWhite.subtle">
                              {getFieldTypeLabel(field.valueType)}
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Text color="surface.text.staticWhite.subtle">
                              {field.isRequired ? 'Yes' : 'No'}
                            </Text>
                          </TableCell>
                          <TableCell textAlign="right">
                            <Box display="flex" justifyContent="flex-end">
                              <Dropdown>
                                <DropdownButton
                                  icon={MoreHorizontalIcon}
                                  variant="tertiary"
                                  size="small"
                                  accessibilityLabel={`Actions for ${field.label}`}
                                />
                                <DropdownOverlay width="220px">
                                  <ActionList>
                                    <ActionListItem
                                      title="Edit field"
                                      value="edit"
                                      leading={<ActionListItemIcon icon={EditIcon} />}
                                      onClick={() => onEdit(field)}
                                    />
                                    <ActionListItem
                                      title="Delete"
                                      value="delete"
                                      leading={<ActionListItemIcon icon={TrashIcon} />}
                                      onClick={() => onDelete(field)}
                                      intent="negative"
                                    />
                                  </ActionList>
                                </DropdownOverlay>
                              </Dropdown>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            )}
          </>
        )}
      </Table>
    </EmployeeFieldsTableSurface>
  );
};
